import { Form } from "antd/es";
import { Rule } from "antd/es/form";
import * as React from "react";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaEditorTitle } from "../../ui-kit/kuika-page-header/kuika-editor-title";
import { ICodeEditorRenameTestIds } from "./code-editor-rename";
import "./code-editor-rename-title.scss";

/**
 * The name row of an editor whose form is antd's: the record's identity and a pencil, and —
 * while renaming — the bound field with confirm and abort.
 *
 * The antd twin of `ConnectionRenameTitle`, and it has to be a separate component: that one
 * binds through rc-field-form's `Field`, which does not bind inside an antd v4 `<Form>` (the two
 * engines keep separate stores), so a rename typed there would be silently dropped.
 *
 * Presentation and keyboard only. The editor owns the name in its state and decides what
 * confirm means — it awaits the field's async rules (`validateRenameField`) before it commits.
 */
export interface ICodeEditorRenameTitleProps {
  /** What the record IS while it is not being renamed: its switcher chip. */
  identity: React.ReactNode;
  isRenaming: boolean;
  /** False while there is no committed name to fall back to, so abort would strand the record. */
  canAbortRename: boolean;
  /** Lower case, for the controls' accessible names: "TypeScript action", "SQL action". */
  entityLabel: string;
  /** The antd field the name is typed into — bound to the editor's own `<Form>`. */
  fieldName: string;
  rules: Rule[];
  placeholder: string;
  onStartRename: () => void;
  onConfirmRename: () => void;
  onAbortRename: () => void;
  testIds: ICodeEditorRenameTestIds;
  inputOnboardingId?: string;
}

export const CodeEditorRenameTitle = ({
  identity,
  isRenaming,
  canAbortRename,
  entityLabel,
  fieldName,
  rules,
  placeholder,
  onStartRename,
  onConfirmRename,
  onAbortRename,
  testIds,
  inputOnboardingId
}: ICodeEditorRenameTitleProps): React.ReactElement => {
  // The field sits inside the editor's `<form>`: Enter there would also submit it — the save —
  // and Escape would reach whatever listens above the bar. Both stop at the field.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      onConfirmRename();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      if (canAbortRename) onAbortRename();
    }
  };

  // Renaming usually replaces the name outright, so the whole of it starts selected.
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
    event.currentTarget.select();
  };

  return (
    <KuikaEditorTitle
      entityLabel={entityLabel}
      identity={identity}
      isRenaming={isRenaming}
      canAbortRename={canAbortRename}
      onStartRename={onStartRename}
      onConfirmRename={onConfirmRename}
      onAbortRename={onAbortRename}
      editTestId={testIds.edit}
      confirmTestId={testIds.confirm}
      abortTestId={testIds.cancel}
      renameField={
        <Form.Item name={fieldName} rules={rules} className="kuika__code-editor__rename-field">
          <KuikaInput
            size="sm"
            autoFocus
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            data-testid={testIds.input}
            data-onboarding-id={inputOnboardingId}
          />
        </Form.Item>
      }
    />
  );
};
